import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import ReturnButton from '../../components/ui/ReturnButton';
import LocationDescription from './sections/DescriptionSection';
import PhotoGallery from './sections/GallerySection';
import LocationDetails from './sections/DetailsSection';
import LocationPoints from './sections/PointSection';
import LocationMap from '../../components/Map/LocationMap';
import Header from '../../components/layout/Header/Header';
import Footer from '../../components/layout/Footer/Footer';

const LocationPage = () => {
  const { id } = useParams();
  console.log('ID из URL:', id);

  const [location, setLocation] = useState(null);
  const [points, setPoints] = useState([]);
  const [additionalInfo, setAdditionalInfo] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const apiUrl = process.env.REACT_APP_API_URL;

        const [locationResponse, pointsResponse, infoResponse] = await Promise.all([
          fetch(`${apiUrl}/locations/${id}`),
          fetch(`${apiUrl}/locations/${id}/points`),
          fetch(`${apiUrl}/locations/${id}/additional-info`)
        ]);

        if (!locationResponse.ok) {
          throw new Error('Локация не найдена');
        }

        const locationData = await locationResponse.json();
        
        let pointsData = [];
        if (pointsResponse.ok) {
          const pointsRaw = await pointsResponse.json();
          pointsData = Array.isArray(pointsRaw) ? pointsRaw : [];
        }
        
        let infoData = [];
        if (infoResponse.ok) {
          const infoRaw = await infoResponse.json();
          infoData = Array.isArray(infoRaw) ? infoRaw : [];
        }

        console.log('Загруженные точки (до фото):', pointsData);

        if (pointsData.length > 0) {
          const pointsWithPhotos = await Promise.all(
            pointsData.map(async (point) => {
              try {
                const photosResponse = await fetch(`${apiUrl}/points/${point.id}/photos`);
                console.log(`Загрузка фото для точки ${point.id}, статус:`, photosResponse.status);
                
                if (photosResponse.ok) {
                  const photosRaw = await photosResponse.json();
                  let photosData = Array.isArray(photosRaw) ? photosRaw : (photosRaw.data || []);
                  
                  console.log(`Фото для точки ${point.id}:`, photosData);
                  
                  const formattedPhotos = photosData.map(photo => ({
                    id: photo.id,
                    link: photo.link || photo.url,
                    url: photo.url || photo.link,
                    sort_order: photo.sort_order
                  }));
                  
                  formattedPhotos.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
                  
                  return { 
                    ...point, 
                    photos: formattedPhotos,
                    photoLinks: formattedPhotos.map(p => p.link)
                  };
                }
              } catch (err) {
                console.warn(`Ошибка загрузки фото для точки ${point.id}:`, err);
              }
              return { ...point, photos: [], photoLinks: [] };
            })
          );
          setPoints(pointsWithPhotos);
          console.log('Точки с фото:', pointsWithPhotos);
        } else {
          setPoints([]);
        }

        setLocation(locationData);
        setAdditionalInfo(infoData);
      
      } catch (err) {
        console.error('Ошибка:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchData();
    }
  }, [id]);

  const getAllLocationPhotos = () => {
    const photos = [];
    if (location?.photos) {
      photos.push(...location.photos);
    }
    if (points.length > 0) {
      points.forEach(point => {
        if (point.photos) {
          photos.push(...point.photos);
        }
      });
    }
    const uniquePhotos = [];
    const seenIds = new Set();
    for (const photo of photos) {
      if (!seenIds.has(photo.id)) {
        seenIds.add(photo.id);
        uniquePhotos.push(photo);
      }
    }
    return uniquePhotos;
  };

  const getPhotoUrl = (photo) => {
    return photo?.url || photo?.link || photo;
  };

  const hasLocationPhotos = () => {
    return location?.photos && location.photos.length > 0;
  };

  const hasDescription = () => {
    return location?.description && location.description.trim() !== '';
  };

  // Функция для получения всех фото для мобильной версии (из галереи локации)
  const getMobileGalleryPhotos = () => {
    // Если есть фото у локации - используем их
    if (location?.photos && location.photos.length > 0) {
      return location.photos.map(p => getPhotoUrl(p));
    }
    // Если нет, пытаемся взять фото из точек
    const pointPhotos = [];
    points.forEach(point => {
      if (point.photos && point.photos.length > 0) {
        pointPhotos.push(getPhotoUrl(point.photos[0]));
      }
    });
    return pointPhotos;
  };

  const formatParagraphs = (text) => {
    if (!text) return [];
    return text.split(/\r?\n|\\n/).filter(p => p.trim() !== '');
  };

  if (loading) return <div>Загрузка локации {id}...</div>;
  if (error) return <div>Ошибка: {error}</div>;
  if (!location) return <div>Локация с ID {id} не найдена</div>;
  
  const paragraphs = location.description
    ? location.description.split(/\r?\n|\\n/).filter(p => p.trim() !== '')
    : [];

  const chunkSize = Math.ceil(paragraphs.length / 3);

  const textBlocks = [
    paragraphs.slice(0, chunkSize),
    paragraphs.slice(chunkSize, chunkSize * 2),
    paragraphs.slice(chunkSize * 2),
  ];

  const mapPoints = points.map(point => ({
    coords: [point.latitude, point.longitude],
    title: point.name,
    description: point.description
  }));

  const allPhotos = getAllLocationPhotos();
  const hasPhotos = hasLocationPhotos();
  const hasDesc = hasDescription();
  const mobileGalleryPhotos = getMobileGalleryPhotos();

  // Компонент заголовка с желтой плашкой
  const LocationHeader = () => (
    <div className="locationNameWrapper flex-row">
      <h1 className="locationName">{location.name}</h1>
      <div className="locationNameYellowUnderneath"></div>
    </div>
  );

  return (
    <div id="container" className="flex-column location-page"> 
      <Header />
      <ReturnButton />

      {/* ДЕСКТОП ВЕРСИЯ */}
      <div className="desktop-layout">
        {hasDesc ? (
          <LocationDescription 
            name={location.name}
            description={location.description}
          />
        ) : (
          <div className="locationInfoWrapper">
            <LocationHeader />
          </div>
        )}
        
        {hasDesc && (
          hasPhotos ? (
            <PhotoGallery photos={allPhotos.map(p => getPhotoUrl(p))} />
          ) : (
            points.length > 0 && <LocationPoints points={points} />
          )
        )}
      </div>

      {/* МОБИЛЬНАЯ ВЕРСИЯ - ИСПРАВЛЕНА */}
      {hasDesc && (
        <div className="mobile-layout">
          {/* БЛОК 1 */}
          <div className="mobile-block">
            <div className="locationNameWrapper mobile-location-name-wrapper">
              <h1 className="locationName">{location.name}</h1>
              <div className="locationNameYellowUnderneath"></div>
            </div>

            <div className="mobile-text">
              <div className="mobile-desc">
                {textBlocks[0]?.map((p, i) => <p key={i}>{p}</p>)}
              </div>
            </div>

            {/* Показываем фото из галереи или из точек */}
            {mobileGalleryPhotos[0] && (
              <img 
                src={mobileGalleryPhotos[0]} 
                alt="Фото 1" 
                className="mobile-photo"
              />
            )}
          </div>

          {/* БЛОК 2 */}
          <div className="mobile-block">
            <div className="mobile-text">
              <div className="mobile-desc">
                {textBlocks[1]?.map((p, i) => <p key={i}>{p}</p>)}
              </div>
            </div>

            {mobileGalleryPhotos[1] && (
              <img 
                src={mobileGalleryPhotos[1]} 
                alt="Фото 2" 
                className="mobile-photo"
              />
            )}
          </div>

          {/* БЛОК 3 */}
          <div className="mobile-block">
            <div className="mobile-text">
              <div className="mobile-desc">
                {textBlocks[2]?.map((p, i) => <p key={i}>{p}</p>)}
              </div>
            </div>

            {mobileGalleryPhotos[2] && (
              <img 
                src={mobileGalleryPhotos[2]} 
                alt="Фото 3" 
                className="mobile-photo"
              />
            )}
          </div>
        </div>
      )}

      {/* Карта — показываем всегда */}
      <LocationMap locations={mapPoints} />
      
      {/* Дополнительная информация */}
      {hasDesc && <LocationDetails details={additionalInfo} />}

      <Footer />
    </div>
  );
};

export default LocationPage;